import { useEffect, useState } from 'react'
import { useNotify } from 'ra-core'
import { Button } from '@internal/ui-kit/components/ui/button'
import { MultiCombobox } from '@internal/ui-kit/components/ui/combobox'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@internal/ui-kit/components/ui/drawer'
import { Label } from '@internal/ui-kit/components/ui/label'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { useBrandOptions } from './useUserProfileData'
import type { inventorySchema } from './schema'
import type { z } from 'zod'

type Inventory = z.infer<typeof inventorySchema>

/**
 * `patchInventory` (`PATCH users/{user_id}/inventories`, `PatchUserInventory`
 * schema) — the create flow's follow-up flagged in MIGRATION-NOTES.md
 * (HATCH-101 round 4). Unlike `AssignInventoryDrawer`'s create body, the real
 * PATCH body only accepts `inventory_id`, `quantity`, and `brand_ids` — no
 * `product_ids`/`tags` field exists on this endpoint. Round 2 review
 * (HATCH-235): `quantity`/`original_quantity` must not be patchable at all
 * (they're derived/tracked server-side, not admin-editable) — only `brand_ids`
 * is submitted, and `original_quantity` is now shown read-only in the table
 * instead.
 */
export function EditInventoryDrawer({ userId, inventory, onUpdated }: { userId: string; inventory: Inventory; onUpdated: () => void }) {
  const [open, setOpen] = useState(false)
  const [brandIds, setBrandIds] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()

  const { brands } = useBrandOptions()

  useEffect(() => {
    if (!open) return
    setBrandIds(inventory.brand_ids ?? [])
  }, [open, inventory])

  const submit = async () => {
    setIsSubmitting(true)
    try {
      await httpClient(`/users/${userId}/inventories`, {
        method: 'PATCH',
        body: JSON.stringify({
          inventory_id: inventory.inventory_id,
          brand_ids: brandIds,
        }),
      })
      notify('Inventory updated', { type: 'success' })
      setOpen(false)
      onUpdated()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not update this inventory.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="right">
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}>
        Edit
      </Button>
      <DrawerContent className="w-full sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>Edit inventory</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1.5">
            <Label>Brands</Label>
            <MultiCombobox
              options={brands.map((brand) => ({ value: brand.id, label: brand.name ?? brand.id }))}
              value={brandIds}
              onChange={setBrandIds}
              placeholder="Select brands"
            />
          </div>
        </div>
        <DrawerFooter>
          <Button type="button" disabled={isSubmitting} onClick={submit}>
            Save
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
